import { stepEndsAfterStart } from '@/lib/listings/publication-templates/resolve';
import z from 'zod/v4';
import { DefaultPresetSchema } from './default-preset-schema';
import type { PresetConfig } from './types';
import { createPresetSchema } from './utils';

/**
 * A publiceringsmall: which queues a listing is published to and when, in
 * days relative to Dag 0 (the day the listing is published). One template
 * belongs to exactly one segment and one object type; at most one per
 * combination is "förvald". Queues are stored by id and looked up again
 * when shown.
 */

export const LISTING_PUBLICATION_TEMPLATE_KINDS = [
  'apartment',
  'industrialpremises',
  'parkingspot',
] as const;

export type ListingPublicationTemplateKind =
  (typeof LISTING_PUBLICATION_TEMPLATE_KINDS)[number];

/** 'HH:mm', 24-hour clock. */
const TimeSchema = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/);

export const ListingPublicationStepSchema = z.object({
  queueId: z.string().min(1),
  /** Days after Dag 0. */
  startDay: z.number().int().min(0),
  startTime: TimeSchema,
  /** Days after `startDay`; null = tills vidare (never unpublished). */
  days: z.number().int().min(0).nullable(),
  /** Ignored (and null) when `days` is null. */
  endTime: TimeSchema.nullable(),
});

export type ListingPublicationStep = z.infer<
  typeof ListingPublicationStepSchema
>;

export const ListingPublicationTemplateDataSchema = z
  .object({
    name: z.string().trim().min(1),
    kind: z.enum(LISTING_PUBLICATION_TEMPLATE_KINDS),
    /** Every queue in the template has this queue type. */
    queueType: z.string().min(1),
    segmentId: z.string().min(1),
    isDefault: z.boolean(),
    steps: z.array(ListingPublicationStepSchema).min(1),
  })
  .superRefine((data, ctx) => {
    const seen = new Set<string>();
    data.steps.forEach((step, index) => {
      if (seen.has(step.queueId)) {
        ctx.addIssue({
          code: 'custom',
          path: ['steps', index, 'queueId'],
          message: 'Samma kö kan bara förekomma en gång i en mall.',
        });
      }
      seen.add(step.queueId);

      if (!stepEndsAfterStart(step)) {
        ctx.addIssue({
          code: 'custom',
          path: ['steps', index, 'days'],
          message: 'Avpublicering måste ske efter publicering.',
        });
      }
    });
  });

export type ListingPublicationTemplateData = z.infer<
  typeof ListingPublicationTemplateDataSchema
>;

export const V1Schema = createPresetSchema(
  '1',
  ListingPublicationTemplateDataSchema
);

export const LISTING_PUBLICATION_TEMPLATE_PRESET_IDENTIFIER =
  'listing.publication-template';

const ListingPublicationTemplatePresetSchema = z.object({
  ...DefaultPresetSchema.shape,
  context: z.literal(LISTING_PUBLICATION_TEMPLATE_PRESET_IDENTIFIER),
  data: V1Schema,
});

export const ListingPublicationTemplatePreset = {
  identifier: LISTING_PUBLICATION_TEMPLATE_PRESET_IDENTIFIER,
  currentSchema: ListingPublicationTemplatePresetSchema,
  currentVersion: '1',
  migrations: [],
  single: false,
} as const satisfies PresetConfig;

export type ListingPublicationTemplatePreset = z.infer<
  typeof ListingPublicationTemplatePresetSchema
>;
